import "../CSS/style.css";
import "../CSS/markdown.css";
import "react-tooltip/dist/react-tooltip.css";
import "react-toastify/dist/ReactToastify.css";

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ndjsonStream from "can-ndjson-stream";
import strings from "../String";
import { Tooltip } from "react-tooltip";
import { ToastContainer, toast } from "react-toastify";
import axiosPythiaInstance from "../axiosPythiaInstance";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { pickRandomQuestions } from "../Constants/DefaultQuestions";
import MarkdownMessage from "./Markdown/MarkdownMessage";
import CitationsBar from "./Markdown/CitationsBar";
import useConnectivity from "../useConnectivity";

const PYTHIA_CONV_KEY = "pythiaConversationId";
const PYTHIA_PREV_RESP_KEY = "pythiaPreviousResponseId";

// True when running inside a WebView2 host (Cimatron desktop app). False for
// the public browser version, where the page must bootstrap its own bearer
// token + speech config and must not persist conversation history.
const isWebView = typeof window !== "undefined" && !!window.chrome?.webview;

function toggleMenu(event) {
  if (event) event.stopPropagation();
  const body = document.getElementsByTagName("body")[0];
  if (body.classList.contains("menu-open")) {
    body.classList.remove("menu-open");
  } else {
    body.classList.add("menu-open");
  }
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function PythiaChat() {
  const [messages, setMessages] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionDetails, setSessionDetails] = useState([]);
  const [themeTogglevisibility, setThemeToggleVisibility] = useState(true);
  const [chatThreads, setChatThreads] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const navigate = useNavigate();

  const chatWindowClearChatText = useRef();
  const sidebarSearchClearText = useRef();

  let query = useQuery();
  let question = query ? query.get("question") : null;

  const { isOnline, reportNetworkFailure } = useConnectivity();

  if (isWebView) {
    window.chrome.webview.addEventListener("message", async (event) => {
      const message = event.data;
      localStorage.setItem("authToken", message.token);
      if (message.subscriptionKey) localStorage.setItem("subscriptionKey", message.subscriptionKey);
      if (message.region) localStorage.setItem("region", message.region);
      fetchChatThreads();
    });
  }

  const fetchChatThreads = async () => {
    // Browser-public mode has no sidebar and no persisted history — nothing
    // to fetch.
    if (!isWebView) return;
    try {
      const response = await axiosPythiaInstance.get("/PythiaReason/history");
      // Controller returns: [{ conversation_id, name, is_pinned, pinned_date,
      //   created_date, turns: [...] }], already sorted pinned-first.
      const threads = (response.data || []).map((c) => {
        const firstTurn = c.turns && c.turns.length > 0 ? c.turns[0] : null;
        return {
          sessionID: c.conversation_id,
          oldestRequest: c.name || (firstTurn ? firstTurn.userMessage : c.conversation_id),
          timestamp: c.created_date,
          isPinned: !!c.is_pinned,
          pinnedDate: c.pinned_date || null,
        };
      });
      setChatThreads(threads);
    } catch (error) {
      console.error("Error fetching chat threads:", error);
    }
  };

  const renameConversation = async (sessionId, newName) => {
    const trimmed = (newName || "").trim();
    if (!trimmed) return;
    try {
      await axiosPythiaInstance.patch(
        `/PythiaReason/history/${sessionId}/name`,
        { name: trimmed }
      );
      fetchChatThreads();
      toast.success("Renamed", {
        position: "top-right",
        autoClose: 1200,
        hideProgressBar: true,
        closeOnClick: true,
        theme: "colored",
      });
    } catch (error) {
      const msg = error.response?.data;
      toast.warn(
        typeof msg === "string" && msg ? msg : "Could not rename. Please try again.",
        {
          position: "top-right",
          autoClose: 2500,
          closeOnClick: true,
          theme: "colored",
        }
      );
    }
  };

  const togglePin = async (sessionId, currentlyPinned, e) => {
    if (e) e.stopPropagation();
    try {
      await axiosPythiaInstance.patch(
        `/PythiaReason/history/${sessionId}/pin`,
        { is_pinned: !currentlyPinned }
      );
      fetchChatThreads();
      toast.success(currentlyPinned ? "Unpinned" : "Pinned", {
        position: "top-right",
        autoClose: 1200,
        hideProgressBar: true,
        closeOnClick: true,
        theme: "colored",
      });
    } catch (error) {
      const msg = error.response?.data;
      const isLimit = typeof msg === "string" && msg.toLowerCase().includes("maximum of 5");
      toast.warn(
        isLimit ? msg : "Could not update pin. Please try again.",
        {
          position: "top-right",
          autoClose: 2500,
          hideProgressBar: false,
          closeOnClick: true,
          theme: "colored",
        }
      );
    }
  };

  const clearText = () => {
    if (chatWindowClearChatText.current) {
      chatWindowClearChatText.current.clearText();
    }
  };

  const createNewChat = async () => {
    setSessionDetails([]);
    localStorage.setItem(PYTHIA_CONV_KEY, "");
    localStorage.setItem(PYTHIA_PREV_RESP_KEY, "");
    const body = document.getElementsByTagName("body")[0];
    if (body.classList.contains("menu-open")) {
      toggleMenu(null);
    }
    clearText();
  };

  const loadChat = (chatId) => {
    const chat = JSON.parse(localStorage.getItem(chatId));
    setMessages(chat.messages);
    setCurrentChatId(chatId);
  };

  // Convert API ChatResponse (final-chunk JSON string) into renderable parts
  const parseHistoricalAssistant = (assistantResponseJson) => {
    try {
      const parsed = JSON.parse(assistantResponseJson);
      return {
        markdown: parsed.response || "",
        citations: parsed.citations || [],
        suggestedQuestions: parsed.suggested_questions || [],
      };
    } catch (e) {
      return {
        markdown: assistantResponseJson || "",
        citations: [],
        suggestedQuestions: [],
      };
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const response = await axiosPythiaInstance.get(`/PythiaReason/history/${sessionId}`);
      // Controller returns flat list of turns ordered by CreatedDate ASC
      const turns = response.data || [];
      const lastTurn = turns.length > 0 ? turns[turns.length - 1] : null;

      localStorage.setItem(PYTHIA_CONV_KEY, sessionId);
      if (lastTurn) {
        localStorage.setItem(PYTHIA_PREV_RESP_KEY, lastTurn.responseId || "");
      }

      const details = turns.map((t) => {
        const parsed = parseHistoricalAssistant(t.assistantResponse);
        return {
          id: t.id,
          request: t.userMessage,
          markdown: parsed.markdown,
          citations: parsed.citations,
          suggestedQuestions: parsed.suggestedQuestions,
          responseId: t.responseId,
          feedbackId: t.feedbackId,
          createdDate: t.createdDate,
          userReaction: t.userReaction,
        };
      });

      setSessionDetails(details);
      const body = document.getElementsByTagName("body")[0];
      if (body.classList.contains("menu-open")) {
        toggleMenu(null);
      }
    } catch (error) {
      console.error("Error fetching session details:", error);
    }
  };

  const requestDeleteConversation = (sessionId, name, e) => {
    if (e) e.stopPropagation();
    setPendingDelete({ sessionId, name: name || "" });
  };

  const cancelDeleteConversation = () => {
    setPendingDelete(null);
  };

  const confirmDeleteConversation = async () => {
    if (!pendingDelete) return;
    const { sessionId } = pendingDelete;
    setPendingDelete(null);
    try {
      await axiosPythiaInstance.delete(`/PythiaReason/history/${sessionId}`);
      if (localStorage.getItem(PYTHIA_CONV_KEY) === sessionId) {
        localStorage.setItem(PYTHIA_CONV_KEY, "");
        localStorage.setItem(PYTHIA_PREV_RESP_KEY, "");
        setSessionDetails([]);
      }
      fetchChatThreads();
      toast.success("Conversation deleted", {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: true,
        closeOnClick: true,
        theme: "colored",
      });
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.warn("Could not delete. Please try again.", {
        position: "top-right",
        autoClose: 2500,
        closeOnClick: true,
        theme: "colored",
      });
    }
  };

  useEffect(() => { }, [sessionDetails]);

  useEffect(() => {
    // Page refresh = new conversation, regardless of mode. Drop any stale
    // conversation pointers so the first message hits /start (not /continue).
    localStorage.removeItem(PYTHIA_CONV_KEY);
    localStorage.removeItem(PYTHIA_PREV_RESP_KEY);

    if (isWebView) {
      // WebView host pushes the token/speech config via postMessage. Just load
      // history once any token already in storage has been written.
      fetchChatThreads();
      return;
    }

    // Browser-public mode: fetch a bearer token from the connector's auth
    // proxy, then fetch the speech config. No history call — ever.
    (async () => {
      try {
        const tokenRes = await fetch(strings.publicTokenUrl);
        if (!tokenRes.ok) {
          console.error("Public token endpoint returned", tokenRes.status);
          return;
        }

        // The auth server returns the JWT either as a raw string ("eyJ…") or
        // wrapped in JSON ({"token":"eyJ…"}). Read as text and handle both.
        const raw = (await tokenRes.text()).trim();
        let token = null;
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed === "string") {
            token = parsed;
          } else if (parsed && typeof parsed === "object") {
            token =
              parsed.token ||
              parsed.access_token ||
              parsed.authToken ||
              parsed.Token ||
              parsed.AuthToken ||
              parsed.jwt ||
              parsed.JWT ||
              parsed.bearerToken ||
              parsed.BearerToken ||
              null;
          }
        } catch {
          // Not JSON — treat the entire body as the raw token (strip optional
          // surrounding quotes the server may have added).
          token = raw.replace(/^"|"$/g, "");
        }

        if (!token) {
          console.error("Public token endpoint returned no usable token. Body was:", raw);
          return;
        }
        localStorage.setItem("authToken", token);
        console.log("authToken saved to localStorage. Fetching speech config…");

        const cfgRes = await fetch(strings.speechConfigUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cfgRes.ok) {
          console.error("Speech config endpoint returned", cfgRes.status);
          return;
        }
        const cfg = await cfgRes.json();
        const subKey = cfg.subscriptionKey || cfg.SubscriptionKey;
        const region = cfg.region || cfg.Region;
        if (subKey) localStorage.setItem("subscriptionKey", subKey);
        if (region) localStorage.setItem("region", region);
      } catch (err) {
        console.error("Browser-mode bootstrap failed:", err);
        // The offline modal will surface via the heartbeat if this is a
        // connectivity problem rather than a config problem.
      }
    })();
  }, []);

  return (
    <>
      <div id="comment_overLay"></div>
      {isWebView && (
        <div className="hamburger">
          <span className="menu-toggle" onClick={(e) => toggleMenu(e)}>
            toggle menu
          </span>
        </div>
      )}
      <div id="body-overlay" onClick={(e) => toggleMenu(e)}></div>
      <ToastContainer style={{ zIndex: 99999 }} />
      {isWebView && (
        <div className="flex-shrink-0 overflow-x-hidden bg-token-sidebar-surface-primary w-300 sidebarMainDiv">
          <SideBar
            onNewChat={createNewChat}
            onLoadChat={loadChat}
            onSelectSession={fetchSessionDetails}
            onDeleteSession={requestDeleteConversation}
            onTogglePin={togglePin}
            onRename={renameConversation}
            fetchChatThreads={fetchChatThreads}
            chatThreads={chatThreads}
            clearText={clearText}
            clearSearchTextref={sidebarSearchClearText}
            isOnline={isOnline}
          />
        </div>
      )}
      <div
        className="relative flex h-full max-w-full flex-1 flex-col overflow-hidden mainChatwindowDiv"
        style={!isWebView ? { width: "100%", marginLeft: 0 } : undefined}
      >
        <main
          className="relative h-full w-full flex-1 overflow-hidden transition-width"
          style={
            !isWebView
              ? { maxWidth: "1100px", margin: "0 auto", width: "100%" }
              : undefined
          }
        >
          <ChatWindow
            messages={messages}
            chatId={currentSessionId}
            sessionDetails={sessionDetails}
            themetoggleVisibility={setThemeToggleVisibility}
            fetchSessionDetails={fetchSessionDetails}
            fetchChatThreads={fetchChatThreads}
            question={question}
            clearChatInputref={chatWindowClearChatText}
            isOnline={isOnline}
            reportNetworkFailure={reportNetworkFailure}
          />
        </main>
      </div>
      <DeleteConfirmModal
        pending={pendingDelete}
        onConfirm={confirmDeleteConversation}
        onCancel={cancelDeleteConversation}
      />
      <OfflineModal isOnline={isOnline} />
    </>
  );
}

const DeleteConfirmModal = ({ pending, onConfirm, onCancel }) => {
  useEffect(() => {
    if (!pending) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") onConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending, onConfirm, onCancel]);

  if (!pending) return null;
  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 10001 }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-conv-title"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="delete-conv-title">Delete conversation?</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={onCancel}
              ></button>
            </div>
            <div className="modal-body">
              <p>
                This will permanently remove{" "}
                <strong style={{ wordBreak: "break-word" }}>
                  {pending.name ? `"${pending.name}"` : "this conversation"}
                </strong>{" "}
                and all its messages. This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onCancel}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={onConfirm} autoFocus>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 10000 }}
        onClick={onCancel}
      />
    </>
  );
};

const OfflineModal = ({ isOnline }) => {
  // Block Escape, Enter, Tab so the user cannot dismiss or skip past the modal
  // via the keyboard while offline.
  useEffect(() => {
    if (isOnline) return;
    const swallow = (e) => {
      if (["Escape", "Enter", "Tab"].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", swallow, true);
    return () => window.removeEventListener("keydown", swallow, true);
  }, [isOnline]);

  if (isOnline) return null;
  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block", zIndex: 100001 }}
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="offline-title"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header" style={{ borderBottom: "none" }}>
              <h5 className="modal-title" id="offline-title">
                <span
                  className="material-icons"
                  style={{ verticalAlign: "middle", marginRight: "8px", color: "#d9534f" }}
                >
                  wifi_off
                </span>
                No internet connection
              </h5>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: "8px" }}>
                Cimatron Copilot is currently offline.
              </p>
              <p style={{ marginBottom: "0", opacity: 0.8 }}>
                Please check your network connection. Copilot will resume
                automatically once the connection is restored.
              </p>
              <div
                style={{
                  marginTop: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: 0.7,
                  fontSize: "13px",
                }}
              >
                <div className="typing-animation" style={{ display: "inline-flex" }}>
                  <div className="typing-dot" style={{ "--delay": "0.2s" }}></div>
                  <div className="typing-dot" style={{ "--delay": "0.3s" }}></div>
                  <div className="typing-dot" style={{ "--delay": "0.4s" }}></div>
                </div>
                <span>Reconnecting…</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 100000, opacity: 0.7 }}
      />
    </>
  );
};

const SideBar = forwardRef(({
  onNewChat,
  onLoadChat,
  onSelectSession,
  onDeleteSession,
  onTogglePin,
  onRename,
  fetchChatThreads,
  chatThreads,
  clearText,
  clearSearchTextref,
  isOnline,
}) => {
  const [filteredChatThreads, setFilteredChatThreads] = useState(chatThreads);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const navigate = useNavigate();

  useImperativeHandle(clearSearchTextref, () => ({
    clearSearchText: () => setSearchTerm(""),
  }));

  const startEdit = (thread, e) => {
    if (e) e.stopPropagation();
    setEditingSessionId(thread.sessionID);
    setEditingValue(thread.oldestRequest || "");
  };

  const cancelEdit = () => {
    setEditingSessionId(null);
    setEditingValue("");
  };

  const commitEdit = async (originalName) => {
    const trimmed = (editingValue || "").trim();
    const sessionId = editingSessionId;
    cancelEdit();
    if (!sessionId || !trimmed || trimmed === originalName) return;
    await onRename(sessionId, trimmed);
  };

  useEffect(() => {
    const term = searchTerm.toLowerCase();
    if (term) {
      const filtered = chatThreads.filter((thread) =>
        (thread.oldestRequest || "").toLowerCase().includes(term)
      );
      setFilteredChatThreads(filtered);
    } else {
      setFilteredChatThreads(chatThreads);
    }
  }, [searchTerm, chatThreads]);

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  const handleButtonClick = () => fetchChatThreads();

  return (
    <nav className="navbar navbar-expand-lg real-menu d-flex flex-column" role="navigation">
      <div className="sidebar-listIteam">
        <span className="closebtn close-btn" onClick={(e) => toggleMenu(e)}>
          &times;
        </span>
        <a className="navbar-brand navbar-start">
          <img
            alt="logo"
            id="logo-img"
            className="logo"
            src="/Img/cimatron-logo-black-2025.svg"
          />
        </a>
        <div className="d-flex flex-row py-4 px-1">
          <div className="threads">
            Threads
            <span
              data-tooltip-id="threads-info-tooltip"
              className="material-icons"
              style={{
                fontSize: "16px",
                marginLeft: "6px",
                verticalAlign: "middle",
                opacity: 0.7,
                cursor: "help",
              }}
            >
              info
            </span>
            <Tooltip
              id="threads-info-tooltip"
              place="right"
              content="Conversation threads are automatically deleted after 30 days."
              style={{ maxWidth: "260px", whiteSpace: "normal", zIndex: 9999 }}
            />
          </div>
          <div className="new-chat">
            <span
              id="add-btn-sidebar"
              data-tooltip-id="new-chat-tooltip"
              className="material-icons"
              onClick={() => {
                onNewChat();
                setSearchTerm("");
                handleButtonClick();
              }}
            >
              add
            </span>
            <Tooltip id="new-chat-tooltip" place="top" content="New Chat" />
          </div>
        </div>
        <div id="searchInput" className="searchInput">
          <input
            id="search-input"
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            disabled={!isOnline}
          />
          <div id="resultBox" className="resultBox"></div>
          <div className="icon">
            <span className="material-icons">search</span>
          </div>
        </div>
        <ul className="chat-threads">
          {filteredChatThreads.map((thread, idx) => {
            const prev = idx > 0 ? filteredChatThreads[idx - 1] : null;
            const isFirstPinned = thread.isPinned && (!prev || !prev.isPinned);
            const isFirstUnpinned =
              !thread.isPinned &&
              prev &&
              prev.isPinned;
            return (
              <React.Fragment key={thread.sessionID}>
                {isFirstPinned && (
                  <li className="thread-section-label" onClick={(e) => e.stopPropagation()}>
                    <span className="material-icons" style={{ fontSize: "13px", marginRight: "4px", verticalAlign: "middle" }}>
                      push_pin
                    </span>
                    PINNED
                  </li>
                )}
                {isFirstUnpinned && (
                  <li className="thread-section-label" onClick={(e) => e.stopPropagation()}>
                    RECENT
                  </li>
                )}
                <li
                  onClick={() => {
                    if (editingSessionId === thread.sessionID) return;
                    clearText();
                    setSearchTerm("");
                    onSelectSession(thread.sessionID);
                  }}
                  className={thread.isPinned ? "thread-pinned" : ""}
                >
                  <small>{thread.timestamp ? new Date(thread.timestamp).toLocaleString() : ""}</small>
                  <br />
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: "6px" }}>
                    {editingSessionId === thread.sessionID ? (
                      <input
                        type="text"
                        value={editingValue}
                        autoFocus
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setEditingValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitEdit(thread.oldestRequest);
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelEdit();
                          }
                        }}
                        onBlur={() => commitEdit(thread.oldestRequest)}
                        className="thread-rename-input"
                        maxLength={120}
                      />
                    ) : (
                      <span
                        data-tooltip-id="thread-title-tooltip"
                        data-tooltip-content={thread.oldestRequest || ""}
                        style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                      >
                        {thread.oldestRequest}
                      </span>
                    )}
                    {editingSessionId !== thread.sessionID && (
                      <>
                        <span
                          data-tooltip-id="pin-thread-tooltip"
                          data-tooltip-content={thread.isPinned ? "Unpin" : "Pin"}
                          className="material-icons"
                          style={{
                            fontSize: "16px",
                            cursor: "pointer",
                            color: thread.isPinned ? "var(--color-primary)" : "var(--icon-color)",
                            opacity: thread.isPinned ? 1 : 0.6,
                          }}
                          onClick={(e) => onTogglePin(thread.sessionID, thread.isPinned, e)}
                        >
                          push_pin
                        </span>
                        <span
                          data-tooltip-id="rename-thread-tooltip"
                          className="material-icons"
                          style={{ fontSize: "16px", cursor: "pointer", opacity: 0.7 }}
                          onClick={(e) => startEdit(thread, e)}
                        >
                          edit
                        </span>
                        <span
                          data-tooltip-id="delete-thread-tooltip"
                          className="material-icons"
                          style={{ fontSize: "16px", cursor: "pointer" }}
                          onClick={(e) => onDeleteSession(thread.sessionID, thread.oldestRequest, e)}
                        >
                          delete
                        </span>
                      </>
                    )}
                  </span>
                </li>
              </React.Fragment>
            );
          })}
        </ul>
        <Tooltip id="pin-thread-tooltip" place="top" />
        <Tooltip id="rename-thread-tooltip" place="top" content="Rename" />
        <Tooltip id="delete-thread-tooltip" place="top" content="Delete conversation" />
        <Tooltip
          id="thread-title-tooltip"
          place="right"
          delayShow={400}
          style={{ maxWidth: "320px", whiteSpace: "normal", wordBreak: "break-word", zIndex: 9999 }}
        />
      </div>
    </nav>
  );
});

const ChatWindow = forwardRef(({
  sessionDetails,
  themetoggleVisibility,
  fetchSessionDetails,
  fetchChatThreads,
  question,
  clearChatInputref,
  isOnline,
  reportNetworkFailure,
}) => {
  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [tempMessage, setTempMessage] = useState("");
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isLikeDislikeClick, setIsLikeDislikeClick] = useState(false);
  const [suggestedquestions, setSuggestedQuestions] = useState([]);
  const [defaultStarterQuestions, setDefaultStarterQuestions] = useState([]);

  const chatWindowRef = useRef(null);
  const wholeChatWindowRef = useRef(null);

  useEffect(() => {
    setDefaultStarterQuestions(pickRandomQuestions(4));
  }, []);

  const startRecognition = () => {
    if (isListening) return;
    const subKey = localStorage.getItem("subscriptionKey");
    const region = localStorage.getItem("region");
    if (!subKey || !region) {
      console.warn("Azure Speech subscription not configured");
      return;
    }
    const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(subKey, region);
    speechConfig.speechRecognitionLanguage = "en-US";
    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);
    recognizer.sessionStarted = () => setIsListening(true);
    recognizer.sessionStopped = () => setIsListening(false);
    recognizer.recognizeOnceAsync(
      (result) => {
        setIsListening(false);
        if (result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          setChatInput(result.text);
        } else if (result.reason === SpeechSDK.ResultReason.Canceled) {
          const cancellationDetails = result.cancellationDetails;
          console.log(`Speech canceled: ${cancellationDetails.reason}`);
        }
      },
      (err) => {
        setIsListening(false);
        console.log(`Speech error: ${err}`);
      }
    );
  };

  const stopRecognition = () => { };

  useImperativeHandle(clearChatInputref, () => ({
    clearText: () => {
      setChatInput("");
      setSuggestedQuestions([]);
      setMessages([]);
      setDefaultStarterQuestions(pickRandomQuestions(4));
    },
  }));

  useEffect(() => () => setIsListening(false), []);

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [tempMessage]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (question) handleQuestionClick(question);
    }, 100);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionDetails && sessionDetails.length > 0) {
      const initialMessages = [];
      sessionDetails.forEach((detail) => {
        initialMessages.push({
          id: `req-${detail.id}`,
          currentId: "",
          text: detail.request,
          type: "outgoing",
        });
        initialMessages.push({
          id: `res-${detail.id}`,
          currentId: detail.id,
          responseId: detail.responseId,
          feedbackId: detail.feedbackId,
          markdown: detail.markdown,
          citations: detail.citations || [],
          suggestedQuestions: detail.suggestedQuestions || [],
          type: "incoming",
          timestamp: detail.createdDate,
          liked: detail.userReaction === "Like",
          disliked: detail.userReaction === "Dislike",
        });
      });
      setMessages(initialMessages);
      // Show follow-ups from the most recent turn
      const last = sessionDetails[sessionDetails.length - 1];
      setSuggestedQuestions(last.suggestedQuestions || []);
    } else {
      setMessages([]);
      setSuggestedQuestions([]);
    }
  }, [sessionDetails]);

  const copyAssistantMessage = async (message) => {
    try {
      const cleaned = buildPlainTextFromMessage(message);
      await navigator.clipboard.writeText(cleaned);
      toast.success("Copied!", {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const copyUserMessage = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied!", {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  function buildPlainTextFromMessage(message) {
    // Strip the most common markdown syntax so the clipboard gets readable text.
    let text = (message.markdown || "").replace(/\r\n/g, "\n");
    text = text.replace(/```[\w-]*\n?([\s\S]*?)```/g, (_, code) => code.trim());
    text = text.replace(/`([^`]+)`/g, "$1");
    text = text.replace(/^\s{0,3}#{1,6}\s+/gm, "");
    text = text.replace(/\*\*([^*]+)\*\*/g, "$1");
    text = text.replace(/\*([^*]+)\*/g, "$1");
    text = text.replace(/!\[[^\]]*\]\([^)]+\)/g, "");
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1 ($2)");
    text = text.replace(/^\s*[-*]\s+/gm, "• ");
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.trim();

    if (message.citations && message.citations.length > 0) {
      text += "\n\nCitations:\n";
      message.citations.forEach((c, i) => {
        const url = c.external_url && c.external_url.trim() !== "" ? c.external_url : c.url;
        const label = c.original_filename || url || "";
        text += `${i + 1}. ${label}${url && label !== url ? ` — ${url}` : ""}\n`;
      });
    }
    return text.trim();
  }

  const addMessage = (text, type = "incoming", options = {}) => {
    const newMessage = {
      id: parseInt(Math.random() * 10000),
      text,
      type,
      ...options,
    };
    setMessages((prevMessages) => [...prevMessages, newMessage]);
  };

  const showTypingAnimation = (trimmedInput) => {
    addMessage("", "typing");
    sendNonBlockingMessage(trimmedInput);
  };

  const handleStreamFailure = () => {
    // Drop the typing placeholder so the chat doesn't appear hung, then surface
    // the offline modal. The user's outgoing message stays in the transcript.
    setMessages((prevMessages) => prevMessages.filter((m) => m.type !== "typing"));
    setTempMessage("");
    if (reportNetworkFailure) reportNetworkFailure();
  };

  const sendNonBlockingMessage = async (trimmedInput) => {
    const token = localStorage.getItem("authToken");
    const existingConvId = localStorage.getItem(PYTHIA_CONV_KEY);
    const existingPrevRespId = localStorage.getItem(PYTHIA_PREV_RESP_KEY);

    const isContinue = existingConvId && existingConvId.trim() !== "";
    const endpoint = isContinue
      ? "/PythiaReason/document-chat/continue"
      : "/PythiaReason/document-chat/start";

    const body = isContinue
      ? {
        message: trimmedInput,
        conversation_id: existingConvId,
        previous_response_id: existingPrevRespId || null,
        persist: isWebView,
      }
      : { message: trimmedInput, persist: isWebView };

    let response;
    try {
      response = await fetch(`${strings.pythiaApiBaseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        credentials: "include",
      });
    } catch (err) {
      console.error("Network error calling Pythia:", err);
      handleStreamFailure();
      return;
    }

    if (!response.ok) {
      console.error("Pythia API non-OK:", response.status);
      // 5xx / network-edge errors that surface as a non-OK response are also
      // a connectivity signal — let the heartbeat decide whether to lift the
      // modal, but drop the typing placeholder either way.
      setMessages((prevMessages) => prevMessages.filter((m) => m.type !== "typing"));
      setTempMessage("");
      return;
    }

    const theNdJsonStream = ndjsonStream(response.body);
    const reader = theNdJsonStream.getReader();

    let tempResponseText = "";
    let citations = [];
    let suggestedQs = [];
    let feedbackId = "";
    let responseId = "";
    let conversationId = "";
    let isFinal = false;

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const asJson = value || {};

        if (asJson.response !== undefined && asJson.response !== null) {
          tempResponseText = asJson.response;
        }
        if (asJson.citations) citations = asJson.citations;
        if (asJson.suggested_questions) suggestedQs = asJson.suggested_questions;
        if (asJson.feedback_id) feedbackId = asJson.feedback_id;
        if (asJson.response_id) responseId = asJson.response_id;
        if (asJson.conversation_id) conversationId = asJson.conversation_id;
        if (asJson.final === true) isFinal = true;

        setTempMessage(tempResponseText);
        if (!isFinal && tempResponseText !== "") {
          await new Promise((r) => setTimeout(r, 50));
        }
      }
    } catch (err) {
      console.error("Stream aborted:", err);
      handleStreamFailure();
      return;
    }

    if (conversationId) localStorage.setItem(PYTHIA_CONV_KEY, conversationId);
    if (responseId) localStorage.setItem(PYTHIA_PREV_RESP_KEY, responseId);

    setSuggestedQuestions(suggestedQs || []);

    setMessages((prevMessages) =>
      prevMessages.map((msg) =>
        msg.type === "typing"
          ? {
            ...msg,
            markdown: tempResponseText,
            citations,
            suggestedQuestions: suggestedQs || [],
            type: "incoming",
            responseId,
            feedbackId,
          }
          : msg
      )
    );
    setTempMessage("");
    fetchChatThreads();
  };

  const handleLikeDislike = async (feedbackId, action) => {
    if (!feedbackId) {
      console.warn("Missing feedback_id; cannot submit feedback");
      return;
    }
    setIsLikeDislikeClick(true);
    try {
      await axiosPythiaInstance.post("/PythiaReason/document-chat/feedback", {
        feedback_id: feedbackId,
        feedback: action === "like" ? "THUMBS_UP" : "THUMBS_DOWN",
      });
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.feedbackId === feedbackId
            ? { ...msg, liked: action === "like", disliked: action === "dislike" }
            : msg
        )
      );
    } catch (error) {
      console.error(`Error sending ${action} feedback:`, error);
    }
  };

  const toggleSidebar = (feedbackId) => {
    const body = document.getElementsByTagName("body")[0];
    if (body.classList.contains("comment-sidebar-open")) {
      body.classList.remove("comment-sidebar-open");
    } else {
      body.classList.add("comment-sidebar-open");
    }
    setSelectedFeedbackId(feedbackId);
    setSidebarVisible(!sidebarVisible);
    themetoggleVisibility(sidebarVisible);
  };

  useEffect(() => {
    if (chatWindowRef.current) {
      if (messages.length === 0) {
        // Welcome screen / new session / fresh load: start at the top so the
        // "Welcome" heading is visible, not scrolled to the bottom.
        chatWindowRef.current.scrollTop = 0;
      } else if (!isLikeDislikeClick) {
        chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
      }
      setIsLikeDislikeClick(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const renderMessage = (message) => {
    if (message.type === "typing") {
      return (
        <div className="chat-content d-flex flex-row-reverse">
          <div className="chat-details ChatItem-chatText">
            <MarkdownMessage markdown={tempMessage} citations={[]} />
            <div className="typing-animation">
              <div className="typing-dot" style={{ "--delay": "0.2s" }}></div>
              <div className="typing-dot" style={{ "--delay": "0.3s" }}></div>
              <div className="typing-dot" style={{ "--delay": "0.4s" }}></div>
            </div>
          </div>
          <img src="Img/chatbot.png" alt="chatbot-img" />
        </div>
      );
    } else if (message.type === "incoming") {
      return (
        <>
          <div className="chat-content d-flex flex-row-reverse">
            <div className="chat-details ChatItem-chatText">
              <MarkdownMessage
                markdown={message.markdown}
                citations={message.citations}
              />
              <CitationsBar citations={message.citations} />
            </div>
            <img src="Img/chatbot.png" alt="chatbot-img" />
          </div>
          <div className="chat-icon-expert">
            <span
              data-tooltip-id="like-tooltip"
              onClick={() => handleLikeDislike(message.feedbackId, "like")}
              className={`material-icons ${message.liked ? "green" : ""}`}
            >
              thumb_up
            </span>
            <Tooltip id="like-tooltip" place="top" content="Like" />
            <span
              data-tooltip-id="dislike-tooltip"
              onClick={() => handleLikeDislike(message.feedbackId, "dislike")}
              className={`material-icons ${message.disliked ? "red" : ""}`}
            >
              thumb_down
            </span>
            <Tooltip id="dislike-tooltip" place="top" content="Dislike" />
            <span
              data-tooltip-id="copyResponse-tooltip"
              onClick={() => copyAssistantMessage(message)}
              className="material-icons"
            >
              content_copy
            </span>
            <Tooltip id="copyResponse-tooltip" place="top" content="Copy Text" />
          </div>
        </>
      );
    }
    return (
      <>
        <div className="chat-content d-flex flex-row">
          <div className="chat-details ChatItem-chatText">
            <p>{message.text}</p>
          </div>
        </div>
        <div className="chat-icon-customer">
          <span
            data-tooltip-id="copyque-tooltip"
            onClick={() => copyUserMessage(message.text)}
            className="material-icons"
          >
            content_copy
          </span>
          <Tooltip id="copyque-tooltip" place="bottom" content="Copy Content" />
        </div>
      </>
    );
  };

  const handleSendMessage = (e, input) => {
    if (!isOnline) {
      if (e) e.preventDefault();
      return;
    }
    setSuggestedQuestions([]);
    if (e) {
      const keyCode = e.which || e.keyCode;
      if (keyCode === 13 && !e.shiftKey) {
        e.preventDefault();
        const trimmedInput = chatInput.trim();
        if (trimmedInput) {
          addMessage(trimmedInput, "outgoing");
          setChatInput("");
          showTypingAnimation(trimmedInput);
        }
      }
    } else if (input) {
      const trimmedInput = input.trim();
      if (trimmedInput) {
        addMessage(trimmedInput, "outgoing");
        setChatInput("");
        showTypingAnimation(trimmedInput);
      }
    }
  };

  const handleQuestionClick = (q) => {
    if (!isOnline) return;
    handleSendMessage(null, q);
  };

  const showWelcome = messages.length === 0;
  const starterChips = suggestedquestions && suggestedquestions.length > 0
    ? suggestedquestions
    : (showWelcome ? defaultStarterQuestions : []);

  return (
    <div
      role="Chat-Window"
      className="flex h-full flex-col focus-visible:outline-0"
      tabIndex="0"
      ref={wholeChatWindowRef}
    >
      <div className="chat-header" id="chat-header">
        <img
          src="Img/copilot-icon-text-bubble-black-512.png"
          alt="copilot-icon-text-bubble-white"
          className="copilot-icon-chat"
          id="copilot-icon-chat"
        />
        <h3 className="px-2">Cimatron Copilot</h3>
      </div>
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <div className="relative h-full">
          <div className="ChatWindow padding-chatwindow" ref={chatWindowRef}>
            <div className="flex h-full flex-col items-center justify-center">
              {showWelcome && (
                <div className="default-text">
                  <img
                    src="Img/copilot-icon-text-bubble-black-512.png"
                    alt="copilot-icon-text-bubble-white"
                    className="copilot-icon-default"
                    id="copilot-icon-default"
                  />
                  <h1>Welcome to Cimatron Copilot</h1>
                  <p>
                    Your use of Cimatron Copilot is subject to the{" "}
                    <a id="terms-privacyPolicy" href="https://www.cimatron.com/en/cimatron-copilot-terms-of-use" target="_blank" rel="noreferrer">Terms of Use</a>{" "}
                    and{" "}
                    <a id="transparencyNotice" href="https://www.cimatron.com/en/cimatron-copilot-transparency-notice" target="_blank" rel="noreferrer">Transparency Notice</a>.
                    You accept, and agree to comply with, the Terms of Use (and acknowledge the Transparency Notice) by accessing and/or using the Cimatron Copilot.
                    <b>&nbsp;You must not access and/or use the Cimatron Copilot if you do not accept, or agree to comply with, the Terms of Use.</b>
                  </p>
                  <p>The Cimatron Copilot is an AI-powered assistant and is here to help with enquiries about our products and to provide guidance on common trouble shooting issues.</p>
                  <p>Please enter your question to get started.</p>
                </div>
              )}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`chat ${message.type === "outgoing"
                    ? "outgoing ChatItem--customer"
                    : "ChatItem--expert"
                    }`}
                >
                  {renderMessage(message)}
                </div>
              ))}
              {starterChips && starterChips.length > 0 && (
                <div id="preQuestions" className="flex flex-row flex-wrap justify-content-center gap-2">
                  {starterChips.map((item, index) => (
                    <div key={index} onClick={() => handleQuestionClick(item)}>
                      {item}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div id="typing-container" className="w-full padding-typingchatwindow">
        <div className="px-3 text-base">
          <div className="mx-auto flex flex-1 gap-3 text-base">
            <div className="typing-content w-full">
              <div className="typing-textarea input-group">
                <textarea
                  value={chatInput}
                  placeholder={
                    isOnline
                      ? "Shift + Enter for a new line"
                      : "Waiting for internet connection…"
                  }
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSendMessage(e)}
                  style={{ margin: "1px" }}
                  id="chat-type-area"
                  disabled={!isOnline}
                />
                <span
                  id="mic"
                  data-tooltip-id="mic-tooltip"
                  className="material-icons"
                  onClick={
                    isOnline
                      ? (isListening ? stopRecognition : startRecognition)
                      : undefined
                  }
                  style={{
                    cursor: isOnline ? "pointer" : "not-allowed",
                    opacity: isOnline ? 1 : 0.4,
                    pointerEvents: isOnline ? "auto" : "none",
                  }}
                >
                  {isListening ? "mic" : "mic_off"}
                </span>
                <span
                  data-tooltip-id="sendquestion-tooltip"
                  className="material-icons"
                  onClick={isOnline ? () => handleQuestionClick(chatInput) : undefined}
                  id="send-btn"
                  style={{
                    cursor: isOnline ? "pointer" : "not-allowed",
                    opacity: isOnline ? 1 : 0.4,
                    pointerEvents: isOnline ? "auto" : "none",
                  }}
                >
                  send
                </span>
                <Tooltip id="sendquestion-tooltip" place="top" content="Send" />
                <Tooltip id="mic-tooltip" place="top" content="Dictate" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PythiaChat;
